var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
import { findDefaultOverlappingStep, sortTimeframes } from "./biz-utils.js";
import { DEFAULT_INDICATOR_TEMPLATES } from "./constant.js";
import { PositionType } from "./enum.js";
import { TriggerType, OperandSourceType, EditMode } from "./enum.js";
import { FormErrorCode, AppFormError } from "./error.js";
import { TriggerTypeCaption } from "./locales/ko.js";
import isNil from "lodash-es/isNil.js";
import { isValidNumber } from "./number.js";
import intersection from "lodash-es/intersection.js";
function validateSignalTrigger(trigger, strategy) {
  const { type: triggerType, steps } = trigger;
  const isEnter = triggerType === TriggerType.SIGNAL_ENTER;
  const availableSignalTypeIds = strategy.signalTypes.map((item) => item.id);
  for (const [idx, step] of steps.entries()) {
    if (!availableSignalTypeIds.includes(step.signalTypeId)) {
      throw new AppFormError(FormErrorCode.SIGNAL_NOT_SELECTED, { fieldPath: ["steps", idx, "signalTypeId"] });
    }
    if (!step.amount || step.amount < 0 || step.amount > 100) {
      throw new AppFormError(FormErrorCode.AMOUNT_INVALID, { fieldPath: ["steps", idx, "amount"] });
    }
  }
  steps.map((step) => step.signalTypeId).reduce((acc, stId, idx) => {
    if (acc.includes(stId)) {
      throw new AppFormError(FormErrorCode.SIGNAL_DUPLICATE, { fieldPath: ["steps", idx, "signalTypeId"] });
    }
    acc.push(stId);
    return acc;
  }, []);
  if (steps.length > 0) {
    if (isEnter) {
      const sum = steps.reduce((acc, curr) => acc + curr.amount, 0);
      if (sum !== 100) {
        throw new AppFormError(FormErrorCode.ENTER_AMOUNT_SUM_NOT_100, {
          fieldPath: ["steps", steps.length - 1, "amount"],
          triggerType: TriggerTypeCaption[triggerType]
        });
      }
    } else {
      if (steps[steps.length - 1].amount !== 100) {
        throw new AppFormError(FormErrorCode.EXIT_LAST_AMOUNT_NOT_100, {
          fieldPath: ["steps", steps.length - 1, "amount"]
        });
      }
    }
  }
}
__name(validateSignalTrigger, "validateSignalTrigger");
function validateTrailingSteps(steps, parentPath) {
  if (steps.length === 0) {
    return;
  }
  for (const [idx, item] of steps.entries()) {
    if (!item.change || item.change <= 0) {
      throw new AppFormError(FormErrorCode.TRAILING_CHANGE_INVALID, { fieldPath: [...parentPath, idx, "change"] });
    }
    if (!item.amount || item.amount < 0 || item.amount > 100) {
      throw new AppFormError(FormErrorCode.AMOUNT_RATIO_INVALID, { fieldPath: [...parentPath, idx, "amount"] });
    }
  }
  steps.sort((a, b) => a.change - b.change);
  if (steps[steps.length - 1].amount !== 100) {
    throw new AppFormError(FormErrorCode.LAST_AMOUNT_NOT_100, {
      fieldPath: [...parentPath, steps.length - 1, "amount"]
    });
  }
}
__name(validateTrailingSteps, "validateTrailingSteps");
function validateExclusiveExitTrigger(trigger) {
  const { type, steps } = trigger;
  steps.forEach((item, idx) => {
    if (!item.change) {
      throw new AppFormError(FormErrorCode.EXCLUSIVE_CHANGE_INVALID, {
        fieldPath: ["steps", idx, "change"],
        triggerType: TriggerTypeCaption[type]
      });
    }
    if (isNil(item.amount) || item.amount < 0 || item.amount > 100) {
      throw new AppFormError(FormErrorCode.AMOUNT_RATIO_INVALID, { fieldPath: ["steps", idx, "amount"] });
    }
  });
  steps.sort((a, b) => a.change - b.change);
  const triggerEx = trigger;
  triggerEx.down = [];
  triggerEx.up = [];
  steps.forEach((item) => {
    if (item.change < 0) {
      triggerEx.down.push(item);
    } else {
      triggerEx.up.push(item);
    }
  });
  if (triggerEx.down.length === 0) {
    throw new AppFormError(FormErrorCode.STOPLOSS_REQUIRED, { fieldPath: ["steps", 0, "change"] });
  }
  if (triggerEx.down.length > 0 && triggerEx.down[0].amount !== 100) {
    throw new AppFormError(FormErrorCode.STOPLOSS_FIRST_AMOUNT_NOT_100, { fieldPath: ["steps", 0, "amount"] });
  }
  validateTrailingSteps(trigger.trailingSteps, ["trailingSteps"]);
  if (trigger.trailingSteps.length === 0 && triggerEx.up.length > 0) {
    if (triggerEx.up[triggerEx.up.length - 1].amount !== 100) {
      throw new AppFormError(FormErrorCode.TAKEPROFIT_LAST_AMOUNT_NOT_100, {
        fieldPath: ["steps", steps.length - 1, "amount"]
      });
    }
  }
}
__name(validateExclusiveExitTrigger, "validateExclusiveExitTrigger");
function validateOverlappingExitTrigger(trigger) {
  const { steps } = trigger;
  if (steps.length < 1) {
    throw new Error("\uB2E8\uACC4 \uCCAD\uC0B0 \uAD6C\uAC04\uC740 \uBC18\uB4DC\uC2DC 1\uAC1C \uC774\uC0C1\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4.");
  }
  steps.forEach((item, idx) => {
    if (!isValidNumber(item.down.change) || !isValidNumber(item.down.amount)) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_VALUE_INVALID, { fieldPath: ["steps", idx, "down", "change"] });
    }
    if (!isValidNumber(item.up.change) || !isValidNumber(item.up.amount)) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_VALUE_INVALID, { fieldPath: ["steps", idx, "up", "change"] });
    }
    if (item.down.amount < 0 || item.up.amount < 0) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_AMOUNT_INVALID, {
        fieldPath: ["steps", idx, item.down.amount < 0 ? "down" : "up", "amount"]
      });
    }
    if (item.down.amount > 100 || item.up.amount > 100) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_AMOUNT_EXCEED, {
        fieldPath: ["steps", idx, item.down.amount > 100 ? "down" : "up", "amount"]
      });
    }
  });
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].up.change <= steps[i].down.change) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_UP_GREATER_THAN_DOWN, {
        fieldPath: ["steps", i, "up", "change"]
      });
    }
    if (i > 0 && steps[i - 1].up.change < steps[i].down.change) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_DOWN_ORDER_INVALID, {
        fieldPath: ["steps", i, "down", "change"]
      });
    }
    if (i > 0 && steps[i - 1].up.change >= steps[i].up.change) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_UP_ORDER_INVALID, { fieldPath: ["steps", i, "up", "change"] });
    }
    if (i > 0 && steps[i - 1].down.change >= steps[i].down.change) {
      throw new AppFormError(FormErrorCode.OVERLAPPING_DOWN_ORDER_INVALID, {
        fieldPath: ["steps", i, "down", "change"]
      });
    }
  }
  if (steps[0].down.amount !== 100) {
    throw new AppFormError(FormErrorCode.OVERLAPPING_FIRST_DOWN_AMOUNT_NOT_100, {
      fieldPath: ["steps", 0, "down", "amount"]
    });
  }
  validateTrailingSteps(trigger.trailingSteps, ["trailingSteps"]);
  if (trigger.trailingSteps.length === 0 && steps[steps.length - 1].up.amount !== 100) {
    throw new AppFormError(FormErrorCode.OVERLAPPING_LAST_UP_AMOUNT_NOT_100, {
      fieldPath: ["steps", steps.length - 1, "up", "amount"]
    });
  }
  const defaultStep = findDefaultOverlappingStep(steps);
  if (defaultStep < 0) {
    throw new Error("\uC218\uC775\uB960 0%\uAC00 \uD3EC\uD568\uB418\uB294 \uAD6C\uAC04\uC774 \uBC18\uB4DC\uC2DC \uC788\uC5B4\uC57C \uD569\uB2C8\uB2E4.");
  }
  trigger.defaultStep = defaultStep;
}
__name(validateOverlappingExitTrigger, "validateOverlappingExitTrigger");
function createSessionForcedExitTriggerMatchInfo(forcedExitType) {
  return {
    type: TriggerType.FORCED_EXIT,
    target: "session",
    // positionOrderType: getExitPositionOrderType(positionType),
    timestamp: Date.now(),
    forcedExitType,
    stepIndex: 0,
    amountRatio: 100
  };
}
__name(createSessionForcedExitTriggerMatchInfo, "createSessionForcedExitTriggerMatchInfo");
function createPositionTriggerMatchInfo(triggerMatchInfo, position, info) {
  const positionTriggerMatchInfo = {
    ...triggerMatchInfo,
    ignoredBy: void 0,
    orderId: void 0,
    timestamp: info.timestamp,
    price: info.price || (position == null ? void 0 : position.price) || 0,
    // pnl: position?.pnl || 0,
    // roe: position?.roe || 0,
    // netPnl: position?.netPnl || 0,
    // netRoe: position?.netRoe || 0,
    smoothedPrice: info.smoothedPrice || (position == null ? void 0 : position.smoothedPrice) || (position == null ? void 0 : position.price) || 0,
    // smoothedPnl: position?.smoothedPnl || position?.pnl || 0,
    // smoothedRoe: position?.smoothedRoe || position?.roe || 0,
    smoothedNetPnl: (position == null ? void 0 : position.smoothedNetPnl) || (position == null ? void 0 : position.netPnl) || 0,
    smoothedNetRoe: (position == null ? void 0 : position.smoothedNetRoe) || (position == null ? void 0 : position.netRoe) || 0
  };
  return positionTriggerMatchInfo;
}
__name(createPositionTriggerMatchInfo, "createPositionTriggerMatchInfo");
function getReferedIndicators(condition) {
  return condition.operands.flatMap(
    (op) => (
      // 수식이면 수식이 참조하는 지표 배열 반환
      op.type === OperandSourceType.FORMULA && op.formulaParams ? Object.values(op.formulaParams).filter((param) => param.type === OperandSourceType.INDICATOR).map((param) => param.sourceId) : op.type === OperandSourceType.INDICATOR ? op.sourceId : []
    )
  ).filter((indId) => typeof indId === "number" && indId > 0);
}
__name(getReferedIndicators, "getReferedIndicators");
function getAffectedConditions(strategy, indicatorIds) {
  const conditions = strategy.conditions.filter((cond) => {
    const referedIndicators = getReferedIndicators(cond);
    return intersection(indicatorIds, referedIndicators).length > 0;
  });
  return conditions;
}
__name(getAffectedConditions, "getAffectedConditions");
function getAffectedByTimeframe(strategy, timeframe) {
  const indicators = strategy.indicators.filter((ind) => ind.timeframe === timeframe);
  const indicatorIds = indicators.map((ind) => ind.id);
  const affectedConditions = getAffectedConditions(strategy, indicatorIds);
  const conditions = strategy.conditions.filter((cond) => cond.timeframe === timeframe);
  for (const cond of affectedConditions) {
    if (!conditions.includes(cond)) {
      conditions.push(cond);
    }
  }
  return { indicators, conditions };
}
__name(getAffectedByTimeframe, "getAffectedByTimeframe");
function getReferencedShapesIdsByCondition(condition) {
  const ids = [];
  for (const op of condition.operands) {
    if (op.type === OperandSourceType.FORMULA && op.formulaParams) {
      for (const param of Object.values(op.formulaParams)) {
        if (param.type === OperandSourceType.SHAPE && param.sourceId !== void 0) {
          ids.push(param.sourceId);
        }
      }
    } else if (op.type === OperandSourceType.SHAPE && op.sourceId !== void 0) {
      ids.push(op.sourceId);
    }
  }
  return ids;
}
__name(getReferencedShapesIdsByCondition, "getReferencedShapesIdsByCondition");
function getReferencedShapeIds(strategy, symbol, timeframe) {
  const shapeIds = /* @__PURE__ */ new Set();
  const conditions = strategy.conditions.filter(
    (cond) => !cond.disabled && cond.editMode !== EditMode.DELETE && (!timeframe || cond.timeframe === timeframe) && (!symbol || cond.symbol === symbol)
  );
  for (const condition of conditions) {
    getReferencedShapesIdsByCondition(condition).forEach((id) => shapeIds.add(id));
  }
  return shapeIds;
}
__name(getReferencedShapeIds, "getReferencedShapeIds");
function getNewId(arr) {
  return arr.length ? Math.max(...arr.map((c) => c.id)) + 1 : 1;
}
__name(getNewId, "getNewId");
function createDefaultIndicatorsForTimeframe(timeframe, existingIndicators) {
  const startId = getNewId(existingIndicators);
  return DEFAULT_INDICATOR_TEMPLATES.map((template, index) => ({
    ...template,
    id: startId + index,
    timeframe,
    editMode: EditMode.ADD
  }));
}
__name(createDefaultIndicatorsForTimeframe, "createDefaultIndicatorsForTimeframe");
function getFirstExitSignalTypeId(strategy, positionType) {
  const trigger = strategy.triggers[TriggerType.SIGNAL_EXIT][positionType];
  if (!trigger || !trigger.enabled || trigger.steps.length === 0) {
    return void 0;
  }
  const step = trigger.steps[0];
  return step == null ? void 0 : step.signalTypeId;
}
__name(getFirstExitSignalTypeId, "getFirstExitSignalTypeId");
function getFirstEnterSignalTypeId(strategy, positionType) {
  const trigger = strategy.triggers[TriggerType.SIGNAL_ENTER][positionType];
  if (!trigger || !trigger.enabled || trigger.steps.length === 0) {
    return void 0;
  }
  const step = trigger.steps[0];
  return step == null ? void 0 : step.signalTypeId;
}
__name(getFirstEnterSignalTypeId, "getFirstEnterSignalTypeId");
function getReferencedSignalTypeIds(strategy, triggerType, positionType) {
  const triggerTypes = [];
  if (triggerType) {
    triggerTypes.push(triggerType);
  } else {
    triggerTypes.push(TriggerType.SIGNAL_EXIT, TriggerType.SIGNAL_ENTER);
  }
  const positionTypes = [];
  if (positionType) {
    positionTypes.push(positionType);
  } else {
    positionTypes.push(PositionType.LONG, PositionType.SHORT);
  }
  const signalTypeIds = [
    positionTypes.map((positionType2) => {
      return [
        triggerTypes.map((triggerType2) => {
          const trigger = strategy.triggers[triggerType2][positionType2];
          if (trigger && trigger.enabled) {
            return trigger.steps.map((step) => step.signalTypeId);
          }
          return [];
        })
      ];
    })
  ].flat(4).unique();
  const existingSignalTypeIds = strategy.signalTypes.map((st) => st.id);
  const validSignalTypeIds = signalTypeIds.filter((id) => existingSignalTypeIds.includes(id));
  return validSignalTypeIds;
}
__name(getReferencedSignalTypeIds, "getReferencedSignalTypeIds");
function getFilteredStrategyConfig(strategyConfig) {
  const activeSignalTypesIds = getReferencedSignalTypeIds(strategyConfig);
  const activeSignalTypes = strategyConfig.signalTypes.filter((st) => activeSignalTypesIds.includes(st.id));
  const activeConditions = strategyConfig.conditions.filter(
    (cond) => !cond.disabled && cond.editMode !== EditMode.DELETE && cond.usages.some((stId) => activeSignalTypesIds.includes(stId))
  );
  const activeIndicatorIds = [
    ...new Set(
      activeConditions.flatMap(
        (cond) => cond.operands.flatMap(
          (op) => op.type === OperandSourceType.FORMULA && op.formulaParams ? Object.values(op.formulaParams).filter((param) => param.type === OperandSourceType.INDICATOR).map((param) => param.sourceId) : op.type === OperandSourceType.INDICATOR ? op.sourceId : []
        )
      ).filter((indId) => (indId || 0) > 0)
    )
  ];
  const activeIndicators = strategyConfig.indicators.filter((ind) => activeIndicatorIds.includes(ind.id));
  const activeTimeframes = sortTimeframes([...new Set(activeConditions.flatMap((cond) => cond.timeframe))]);
  if (activeTimeframes.length === 0) {
    activeTimeframes.push(strategyConfig.timeframes[0]);
  }
  return {
    ...strategyConfig,
    timeframes: activeTimeframes,
    signalTypes: activeSignalTypes,
    conditions: activeConditions,
    indicators: activeIndicators
  };
}
__name(getFilteredStrategyConfig, "getFilteredStrategyConfig");
export {
  createDefaultIndicatorsForTimeframe,
  createPositionTriggerMatchInfo,
  createSessionForcedExitTriggerMatchInfo,
  getAffectedByTimeframe,
  getAffectedConditions,
  getFilteredStrategyConfig,
  getFirstEnterSignalTypeId,
  getFirstExitSignalTypeId,
  getNewId,
  getReferedIndicators,
  getReferencedShapeIds,
  getReferencedShapesIdsByCondition,
  getReferencedSignalTypeIds,
  validateExclusiveExitTrigger,
  validateOverlappingExitTrigger,
  validateSignalTrigger,
  validateTrailingSteps
};
//# sourceMappingURL=strategy-utils.js.map
